import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  Output,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Store } from '@ngrx/store';
import { AuthService } from '../../../core/services/auth';
import { MobileNavService } from '../../../core/services/mobile-nav';
import { RelatedAccount } from '../../../models/user.model';
import { getCurrentUser, State } from '../../../store';

@Component({
  selector: 'app-account-switcher',
  templateUrl: './account-switcher.component.html',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule, FormsModule],
  host: {
    class: 'flex min-h-0 flex-1 flex-col',
  },
})
export class AccountSwitcherComponent {
  private readonly authService = inject(AuthService);
  private readonly store = inject(Store<State>);
  private readonly mobileNavService = inject(MobileNavService);

  @Input() displayMode: 'overlay' | 'inline' = 'overlay';
  @Output() closeAccountSwitcher = new EventEmitter<void>();
  @Output() accountSelected = new EventEmitter<string>();

  searchQuery = signal<string>('');
  isSwitching = this.mobileNavService.isSwitchingAccount;
  switchError = signal<string | null>(null);

  user = toSignal<any>(this.store.select(getCurrentUser));

  defaultAccountId = computed(() => this.user()?.AccountId ?? null);

  accounts = computed(() => this.user()?.Accounts || []);

  isLoading = computed(() => !this.accounts()?.length);

  filteredAccounts = computed(() => {
    const query = this.searchQuery().toLowerCase();
    const allAccounts = this.accounts();

    if (!query) {
      return allAccounts;
    }

    return allAccounts.filter(
      (account: RelatedAccount) =>
        account.Account.Name.toLowerCase().includes(query) ||
        account.FormattedBillingAddress?.toLowerCase().includes(query),
    );
  });

  onClose(): void {
    this.closeAccountSwitcher.emit();
    this.searchQuery.set('');
  }

  onAccountSelect(accountId: string): void {
    this.mobileNavService.isSwitchingAccount.set(true);
    this.switchError.set(null);

    this.authService.switchAccount(accountId).subscribe({
      next: () => {
        // Clear store data for the old account
        this.store.dispatch({ type: '[App] Reset Account Data' });
        // Load user data for the new account
        this.store.dispatch({ type: '[User] Load Current User' });
        this.accountSelected.emit(accountId);
        this.closeAccountSwitcher.emit();
        this.searchQuery.set('');
        this.mobileNavService.isSwitchingAccount.set(false);
      },
      error: (error) => {
        this.mobileNavService.isSwitchingAccount.set(false);
        if (error.status === 403) {
          this.switchError.set('You do not have access to this account.');
        } else {
          this.switchError.set('Failed to switch account. Please try again.');
        }
      },
    });
  }
}
